import { ProductListingTypes } from "../constants/product";
import { ProjectContainerTypes, ProjectTypes } from "../constants/project";
import type { TProductDetails } from "../hooks/useProductMetadata";
import type { ProjectListGraphQLResponseDataEntity } from "../interfaces/graphql";
import { GetPublishedStatus } from "./getPublishedStatus";
import { DeployedAsSetType, getDeployedAsSet } from "./productDeployedAsSet";

export enum CombinedAttachedProjectsType {
	container = "container",
	operatorOrHelm = "operatorOrHelm",
	openShiftCnf = "openShiftCnf",
	openShiftVirtualization = "openShiftVirtualization",
}
export const getAttachedProjectsCombinedType = (
	certificationListState: ProjectListGraphQLResponseDataEntity[],
): CombinedAttachedProjectsType => {
	const productTypeSet = getDeployedAsSet(certificationListState);
	if (productTypeSet.has(DeployedAsSetType.openShiftCnf)) return CombinedAttachedProjectsType.openShiftCnf;
	else if (productTypeSet.has(DeployedAsSetType.openShiftVirtualization))
		return CombinedAttachedProjectsType.openShiftVirtualization;
	else if (productTypeSet.has(DeployedAsSetType.operator) || productTypeSet.has(DeployedAsSetType.helmChart))
		return CombinedAttachedProjectsType.operatorOrHelm;
	else return CombinedAttachedProjectsType.container;
};

export interface IComponentMetadata {
	atLeastOnePublished: boolean;
	hasCertified: boolean;
	hasValidated: boolean;
	hasPublishedOperators: boolean;
	hasPublishedHelmCharts: boolean;
	hasPublishedContainers: boolean;
	hasPublishedCNIorCSI: boolean;
	isCertifiedProduct: boolean;
	hasLeastOneCertifiedCNF: boolean;
}

export const extractCommonProjectMetadata = (
	activeProjects: ProjectListGraphQLResponseDataEntity[],
): IComponentMetadata => {
	let atLeastOnePublished = false;
	let hasCertified = false;
	let hasValidated = false;
	let hasPublishedOperators = false;
	let hasPublishedHelmCharts = false;
	let hasPublishedContainers = false;
	let hasPublishedCNIorCSI = false;
	let isCertifiedProduct = false;
	let hasLeastOneCertifiedCNF = false;
	activeProjects?.forEach((project) => {
		if (project?.certification_level === "Certified") hasCertified = true;
		if (project?.certification_level === "Vendor validated") hasValidated = true;
		if (
			project?.certification_level === "Certified" ||
			(project?.certification_status === "Published" && project?.certification_level !== "Vendor validated")
		)
			isCertifiedProduct = true;
		if (project?.certification_status === "Certified" && project?.type === ProjectTypes.openShiftCnf)
			hasLeastOneCertifiedCNF = true;

		if (GetPublishedStatus(project) === "Published") {
			atLeastOnePublished = true;
			if (project.type === ProjectTypes.helmChart) hasPublishedHelmCharts = true;
			else if (project.type === ProjectTypes.containers && project.container?.type === ProjectContainerTypes.operator) {
				hasPublishedOperators = true;
				if (project.badges?.length && project.badges.length > 0) hasPublishedCNIorCSI = true;
			} else if (
				project.type === ProjectTypes.containers &&
				(project.container?.type === ProjectContainerTypes.container ||
					project.container?.type === ProjectContainerTypes.scratch)
			) {
				hasPublishedContainers = true;
			}
		}
	});
	return {
		atLeastOnePublished,
		hasCertified,
		hasValidated,
		hasPublishedHelmCharts,
		hasPublishedContainers,
		hasPublishedOperators,
		hasPublishedCNIorCSI,
		isCertifiedProduct,
		hasLeastOneCertifiedCNF,
	};
};
export const getHelmOrOperatorValidator = (
	productTypeSet: Set<DeployedAsSetType>,
	hasPublishedContainers: boolean,
	hasPublishedHelmCharts: boolean,
	hasPublishedOperators: boolean,
) => {
	// at least (one helm/operator) and container projects are 'Published'
	return (
		hasPublishedContainers &&
		((productTypeSet.has(DeployedAsSetType.helmChart) && hasPublishedHelmCharts) ||
			(productTypeSet.has(DeployedAsSetType.operator) && hasPublishedOperators))
	);
};
export const validateAttachedProjects = (
	productDetail: TProductDetails,
	activeCertsList: ProjectListGraphQLResponseDataEntity[],
	includePartnerValidation = true,
): boolean => {
	const {
		hasCertified,
		atLeastOnePublished,
		hasPublishedHelmCharts,
		hasPublishedContainers,
		hasPublishedOperators,
		hasLeastOneCertifiedCNF,
	} = extractCommonProjectMetadata(activeCertsList);
	const isPartnerValidatedProductType =
		productDetail.type === ProductListingTypes.ContainerStack ||
		productDetail.type === ProductListingTypes.TraditionalApplication;
	if (includePartnerValidation && isPartnerValidatedProductType && activeCertsList?.length === 0) {
		// Partner validation Logic
		if (productDetail?.product_validation?.partner_validated === true) {
			return true;
		}
	}
	if (productDetail.type === ProductListingTypes.ContainerStack) {
		const projectTypeSet = getDeployedAsSet(activeCertsList);
		const helmOrOperatorValidator = getHelmOrOperatorValidator(
			projectTypeSet,
			hasPublishedContainers,
			hasPublishedHelmCharts,
			hasPublishedOperators,
		);
		if (projectTypeSet.has(DeployedAsSetType.openShiftCnf)) {
			if (!hasCertified) {
				// Validated CNF project
				// - Validated CNF project attached -> already matches this condition when we are here
				// If helm or operator is attached, do its corresponding validator
				// Make sure that the CNF project is certified. It will go to the next block. This is intentional
				// as per an old requirement from kevin.
				if (projectTypeSet.has(DeployedAsSetType.helmChart) || projectTypeSet.has(DeployedAsSetType.operator)) {
					// Vendor validated cnf with operator or helm attached
					// Do nested operator or helm validator
					return helmOrOperatorValidator && hasLeastOneCertifiedCNF;
				} else if (
					projectTypeSet.has(DeployedAsSetType.containers) ||
					projectTypeSet.has(DeployedAsSetType.rhel) ||
					projectTypeSet.has(DeployedAsSetType.openstackAppContainer)
				) {
					// Vendor validated cnf with just container attached
					// Cannot publish with just container only
					// CONNCERT-2098
					return false;
				} else {
					// Vendor validated cnf with no container or operator or helm attached
					return atLeastOnePublished;
				}
			} else {
				// Certified CNF project
				// - Certified CNF project attached -> already matches this condition when we are here
				// - Operator or Helm chart project attached
				// - At least one container image project is attached
				// - at least one helm/operator and container projects are 'Published'
				return (
					helmOrOperatorValidator &&
					(projectTypeSet.has(DeployedAsSetType.helmChart) || projectTypeSet.has(DeployedAsSetType.operator))
				);
			}
		} else if (projectTypeSet.has(DeployedAsSetType.openShiftVirtualization)) {
			// Openshift Virtualization project
			// If helm or operator is attached, do its corresponding validator
			if (projectTypeSet.has(DeployedAsSetType.helmChart) || projectTypeSet.has(DeployedAsSetType.operator)) {
				// Do nested operator or helm validator
				return helmOrOperatorValidator;
			}
			// Openshift virt without any other components
			// At least of the attached projects are 'Published'
			return atLeastOnePublished;
		} else if (projectTypeSet.has(DeployedAsSetType.helmChart) || projectTypeSet.has(DeployedAsSetType.operator)) {
			// For Helm/operator deployed as: Container
			// should have container image attached (excluding rhel container or openstack app container)
			// All of the required components should be published
			return helmOrOperatorValidator;
		} else {
			// Pure Containers
			// For others: Container image / rhel container / openstack container project attached
			// At least of the attached projects are 'Published'
			return (
				atLeastOnePublished &&
				(projectTypeSet.has(DeployedAsSetType.containers) ||
					projectTypeSet.has(DeployedAsSetType.rhel) ||
					projectTypeSet.has(DeployedAsSetType.openstackAppContainer))
			);
		}
	} else {
		// Has one or more certification projects attached and one of it is published
		return atLeastOnePublished;
	}
};
